import type { IArticleRepository } from "@/core/repositories/interfaces/IArticleRepository";
import type { IMediaService } from "@/core/services/interfaces/IMediaService";
import type { IArticleService } from "@/core/services/interfaces/IArticleService";
import {
  isCancellableArticleDraft,
  isDeletableEditorialStatus,
  type Article,
  type ArticleAdminListItem,
  type ArticleRedactionListItem,
  type ArticleEditor,
  type ArticleLibraryKpis,
  type ArticleLibraryTab,
  type ArticleLibraryTabCounts,
  type ArticleLibrarySortState,
  ARTICLE_SUGGESTION_SEARCH_LIMIT,
  ARTICLE_SUGGESTION_SEARCH_MIN_LENGTH,
  type ArticleListPage,
  type ArticleSuggestionMutationResult,
  type ArticleSuggestionSearchEventsResult,
  type ArticleSuggestionSearchPlacesResult,
  type ArticleSuggestions,
  type ArticleSummary,
  type CancelArticleCreationResult,
  type DeleteSignalContentResult,
  type CreateArticleInput,
  type CreateArticleResult,
  type UpdateArticleContentInput,
  type UpdateArticleInput,
  type UpdateArticleAssignmentInput,
  type UpdateArticleInternalNotesInput,
  type UpdateArticleCoverImageInput,
  type UpdateArticleStatusInput,
  type UploadArticleCoverImageResult,
  type ArticleTaxonomy,
  type UpdateArticleTaxonomyInput,
  type UpdateArticleTaxonomyResult,
} from "@/core/services/types/article.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";

export class ArticleService implements IArticleService {
  constructor(
    private readonly repository: IArticleRepository,
    private readonly mediaService: IMediaService,
  ) {}

  async createArticle(input: CreateArticleInput = {}): Promise<CreateArticleResult> {
    if (input.status === "SIGNAL") {
      const { status: _status, ...createInput } = input;
      const created = await this.repository.createAdmin({
        ...createInput,
        status: "DRAFT",
      });

      if (!created) {
        return { success: false, error: "create_failed" };
      }

      const promoted = await this.repository.updateAdminStatus(created.id, {
        editStatus: "SIGNAL",
      });

      if (!promoted) {
        await this.repository.deleteAdmin(created.id);
        return { success: false, error: "signal_status_failed" };
      }

      return { success: true, data: promoted };
    }

    const created = await this.repository.createAdmin(input);

    if (!created) {
      return { success: false, error: "create_failed" };
    }

    return { success: true, data: created };
  }

  async cancelArticleCreation(id: string): Promise<CancelArticleCreationResult> {
    if (!id.trim()) {
      return { success: false, error: "not_found" };
    }

    const article = await this.repository.findAdminById(id);
    if (!article) {
      return { success: false, error: "not_found" };
    }

    if (!isCancellableArticleDraft(article)) {
      return { success: false, error: "not_cancellable" };
    }

    const deleted = await this.repository.deleteAdmin(id);
    if (!deleted) {
      return { success: false, error: "delete_failed" };
    }

    return { success: true };
  }

  async deleteSignalContent(id: string): Promise<DeleteSignalContentResult> {
    if (!id.trim()) {
      return { success: false, error: "not_found" };
    }

    const article = await this.repository.findAdminById(id);
    if (!article) {
      return { success: false, error: "not_found" };
    }

    if (!isDeletableEditorialStatus(article.status)) {
      return { success: false, error: "not_deletable" };
    }

    const deleted = await this.repository.deleteAdmin(id);
    if (!deleted) {
      return { success: false, error: "delete_failed" };
    }

    return { success: true };
  }

  async listPublished(limit?: number): Promise<ArticleSummary[]> {
    return this.repository.findPublished(limit);
  }

  async getById(id: string): Promise<Article | null> {
    if (!id.trim()) return null;
    return this.repository.findById(id);
  }

  async getForEdit(id: string): Promise<ArticleEditor | null> {
    if (!id.trim()) return null;
    return this.repository.findAdminById(id);
  }

  async updateArticle(
    id: string,
    input: UpdateArticleInput,
  ): Promise<ArticleEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdmin(id, input);
  }

  async updateArticleContent(
    id: string,
    input: UpdateArticleContentInput,
  ): Promise<ArticleEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminContent(id, input);
  }

  async updateArticleStatus(
    id: string,
    input: UpdateArticleStatusInput,
  ): Promise<ArticleEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminStatus(id, input);
  }

  async updateArticleAssignment(
    id: string,
    input: UpdateArticleAssignmentInput,
  ): Promise<ArticleEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminAssignment(id, input);
  }

  async updateArticleInternalNotes(
    id: string,
    input: UpdateArticleInternalNotesInput,
  ): Promise<ArticleEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminInternalNotes(id, input);
  }

  async updateArticleCoverImage(
    id: string,
    input: UpdateArticleCoverImageInput,
  ): Promise<ArticleEditor | null> {
    if (!id.trim()) return null;
    return this.repository.updateAdminCoverImage(id, input);
  }

  async uploadArticleCoverImage(
    id: string,
    file: File,
  ): Promise<UploadArticleCoverImageResult> {
    if (!id.trim()) {
      return { success: false, error: "Missing article id" };
    }

    const uploadResult = await this.mediaService.uploadArticleCoverImage(
      id,
      file,
    );

    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error };
    }

    const updated = await this.repository.updateAdminCoverImage(id, {
      coverImageUrl: uploadResult.publicUrl,
    });

    if (!updated) {
      return {
        success: false,
        error: "Failed to save cover image URL",
      };
    }

    return { success: true, data: updated };
  }

  async listByLibraryTab(
    tab: ArticleLibraryTab,
    page: number,
    pageSize: LibraryPageSize,
    search?: string,
    sort?: ArticleLibrarySortState,
  ): Promise<ArticleListPage> {
    return this.repository.findByLibraryTab(tab, page, pageSize, search, sort);
  }

  async listAssignedInReview(assigneeId: string): Promise<ArticleRedactionListItem[]> {
    if (!assigneeId.trim()) return [];
    return this.repository.findAssignedForRedaction(assigneeId, "PENDING_REVIEW");
  }

  async countByLibraryTab(): Promise<ArticleLibraryTabCounts> {
    return this.repository.countByLibraryTab();
  }

  async getLibraryStats(): Promise<{
    tabCounts: ArticleLibraryTabCounts;
    kpis: ArticleLibraryKpis;
  }> {
    const [tabCounts, publishedThisMonth] = await Promise.all([
      this.repository.countByLibraryTab(),
      this.repository.countPublishedThisMonth(),
    ]);

    return {
      tabCounts,
      kpis: {
        total: tabCounts.ALL,
        pendingReview: tabCounts.PENDING_REVIEW,
        publishedThisMonth,
      },
    };
  }

  async getSuggestions(articleId: string): Promise<ArticleSuggestions> {
    if (!articleId.trim()) {
      return { places: [], events: [] };
    }

    const [places, events] = await Promise.all([
      this.repository.findSuggestedPlacesByArticleId(articleId),
      this.repository.findSuggestedEventsByArticleId(articleId),
    ]);

    return { places, events };
  }

  private async loadSuggestionsOrFail(
    articleId: string,
  ): Promise<ArticleSuggestionMutationResult> {
    const article = await this.repository.findAdminById(articleId);
    if (!article) {
      return { success: false, error: "not_found" };
    }

    const data = await this.getSuggestions(articleId);
    return { success: true, data };
  }

  async addSuggestedPlace(
    articleId: string,
    placeId: string,
  ): Promise<ArticleSuggestionMutationResult> {
    if (!articleId.trim() || !placeId.trim()) {
      return { success: false, error: "invalid_input" };
    }

    const added = await this.repository.addSuggestedPlace(articleId, placeId);
    if (!added) {
      return { success: false, error: "mutation_failed" };
    }

    return this.loadSuggestionsOrFail(articleId);
  }

  async removeSuggestedPlace(
    articleId: string,
    placeId: string,
  ): Promise<ArticleSuggestionMutationResult> {
    if (!articleId.trim() || !placeId.trim()) {
      return { success: false, error: "invalid_input" };
    }

    const removed = await this.repository.removeSuggestedPlace(articleId, placeId);
    if (!removed) {
      return { success: false, error: "mutation_failed" };
    }

    return this.loadSuggestionsOrFail(articleId);
  }

  async addSuggestedEvent(
    articleId: string,
    eventId: string,
  ): Promise<ArticleSuggestionMutationResult> {
    if (!articleId.trim() || !eventId.trim()) {
      return { success: false, error: "invalid_input" };
    }

    const added = await this.repository.addSuggestedEvent(articleId, eventId);
    if (!added) {
      return { success: false, error: "mutation_failed" };
    }

    return this.loadSuggestionsOrFail(articleId);
  }

  async removeSuggestedEvent(
    articleId: string,
    eventId: string,
  ): Promise<ArticleSuggestionMutationResult> {
    if (!articleId.trim() || !eventId.trim()) {
      return { success: false, error: "invalid_input" };
    }

    const removed = await this.repository.removeSuggestedEvent(articleId, eventId);
    if (!removed) {
      return { success: false, error: "mutation_failed" };
    }

    return this.loadSuggestionsOrFail(articleId);
  }

  async searchPlacesForSuggestion(
    query: string,
    excludePlaceIds: string[] = [],
  ): Promise<ArticleSuggestionSearchPlacesResult> {
    const trimmed = query.trim();
    if (trimmed.length < ARTICLE_SUGGESTION_SEARCH_MIN_LENGTH) {
      return { success: true, places: [] };
    }

    const places = await this.repository.searchPlacesForSuggestion(
      trimmed,
      ARTICLE_SUGGESTION_SEARCH_LIMIT,
    );

    const exclude = new Set(excludePlaceIds);
    return {
      success: true,
      places: places.filter((place) => !exclude.has(place.id)),
    };
  }

  async searchEventsForSuggestion(
    query: string,
    excludeEventIds: string[] = [],
  ): Promise<ArticleSuggestionSearchEventsResult> {
    const trimmed = query.trim();
    if (trimmed.length < ARTICLE_SUGGESTION_SEARCH_MIN_LENGTH) {
      return { success: true, events: [] };
    }

    const events = await this.repository.searchEventsForSuggestion(
      trimmed,
      ARTICLE_SUGGESTION_SEARCH_LIMIT,
    );

    const exclude = new Set(excludeEventIds);
    return {
      success: true,
      events: events.filter((event) => !exclude.has(event.id)),
    };
  }

  async getArticleTaxonomy(id: string): Promise<ArticleTaxonomy> {
    if (!id.trim()) {
      return { artFormIds: [], artisticPeriodIds: [], culturalAreaIds: [], exhibitionTypeIds: [] };
    }
    return this.repository.findTaxonomy(id);
  }

  async updateArticleTaxonomy(
    id: string,
    input: UpdateArticleTaxonomyInput,
  ): Promise<UpdateArticleTaxonomyResult> {
    if (!id.trim()) {
      return { success: false, error: "Missing article id" };
    }

    const ok = await this.repository.replaceTaxonomy(id, input);

    if (!ok) {
      return { success: false, error: "Failed to update taxonomy" };
    }

    return { success: true };
  }
}
